import type { Faq } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Badge,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@hostesa/ui';
import { namespace } from './i18n';

/**
 * FaqList — the hotel's approved question/answer pairs, active and inactive
 * (spec 42: served by GET /knowledge/faqs); an empty list renders the
 * guidance empty-state.
 */
export function FaqList({ faqs }: { faqs: readonly Faq[] }) {
  const { t } = useT(namespace);

  if (faqs.length === 0) {
    return (
      <EmptyState
        data-testid="knowledge-faqs-empty"
        title={t('faqs.title')}
        description={t('faqs.empty')}
      />
    );
  }

  return (
    <Table data-testid="knowledge-faq-table">
      <TableHeader>
        <TableRow>
          <TableHead>{t('faqs.question')}</TableHead>
          <TableHead>{t('faqs.answer')}</TableHead>
          <TableHead>{t('faqs.category')}</TableHead>
          <TableHead>{t('faqs.language')}</TableHead>
          <TableHead>{t('faqs.status')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {faqs.map((faq) => (
          <TableRow key={faq.id} data-testid={`knowledge-faq-row-${faq.id}`}>
            <TableCell>
              <span className="font-medium text-[var(--color-charcoal)]">{faq.question}</span>
            </TableCell>
            <TableCell className="text-sm text-[var(--color-stone-mid)]">
              {faq.approvedAnswer}
            </TableCell>
            <TableCell>{t(`category.${faq.category}`)}</TableCell>
            <TableCell>{faq.language}</TableCell>
            <TableCell>
              <Badge
                data-testid={`knowledge-faq-status-${faq.id}`}
                variant={faq.active ? 'default' : 'outline'}
              >
                {faq.active ? t('faqs.active') : t('faqs.inactive')}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
